import React, { createContext, useContext, useState, useEffect } from 'react';
import service from '../service';
import { DateTime } from 'luxon';
import { computeNextWalletRenewal } from '../utils/utils';

const FishContext = createContext();

const FishContextProvider = ({ children }) => {
  const [fishArray, setFishArray] = useState([]);
  const [fishTags, setFishTags] = useState({}); // Store tags by fishId
  const [segmentsForFish, setSegmentsForFish] = useState({});
  const [nextReloadDate, setNextReloadDate] = useState(null);

  const readAllFish = async (query) => {
    try {
      const fish = await service.readAllFish(query);
      setFishArray(fish);

      return fish;
    } catch {
      console.error('Unable to read all fish');
    }
  };

  const readFishById = async (id) => {
    const response = await service.readFishById(id);
    const fish = await response.json();
    return fish;
  };

  const readFishTags = async (fishId) => {
    const tags = await service.readFishTags(fishId);
    setFishTags({
      [fishId]: tags,
    });
    return tags;
  };

  const readSegmentsForFish = async (fishId) => {
    const segments = await service.readSegmentsForFish(fishId);
    setSegmentsForFish({
      [fishId]: segments,
    });
    return segments;
  };

  const getMemberById = async (id) => {
    try {
      const data = await service.readFishById(id, true);
      const member = { ...data };

      //Don't attempt to get wallet renewal if its the Cigar Lounge or any tier without a quarterly spend
      if (
        member?.wallet?.id &&
        member?.subscriptions[0]?.subscriptionTier?.title !== 'Cigar Lounge'
      ) {
        const walletRenewal = await formatNextWalletRenewal(
          member?.subscriptions[0]
        );
        if (walletRenewal) {
          member.wallet = {
            ...member.wallet,
            nextWalletRenewal: walletRenewal,
          };
        }
      }

      if (
        Array.isArray(member.subscriptions) &&
        member?.subscriptions?.length
      ) {
        const activeSubscription = member.subscriptions[0];
        const subscriptionRenewal =
          getNextSubscriptionRenewalDate(activeSubscription);
        if (subscriptionRenewal) {
          member.subscriptions[0] = {
            ...activeSubscription,
            nextSubscriptionRenewal: subscriptionRenewal,
          };
        }

        const purchaseHistoryRes = await getSuccessfulPurchases(
          member?.id,
          'SUCCESS',
        );
        if (purchaseHistoryRes) {
          member.purchasesArray = purchaseHistoryRes;
        }
      }
      return member;
    } catch {
      console.error('Error while retrieving member by id');
    }
  };

  const formatNextWalletRenewal = async (subscription) => {
    const nextWalletRenewalDate = computeNextWalletRenewal(subscription);
    const formattedExpiresAt = DateTime.fromISO(nextWalletRenewalDate)
      .toLocal()
      .toFormat('yyyy-LL-dd');

    setNextReloadDate(formattedExpiresAt);
    return formattedExpiresAt;
  };

  const getNextSubscriptionRenewalDate = (subscription) => {
    let startingTime;
    let endsAt;

    if (subscription?.subscriptionRenewedAt) {
      startingTime = DateTime.fromISO(subscription?.subscriptionRenewedAt);
    } else {
      startingTime = DateTime.fromISO(subscription?.beginsAt);
    }

    if (subscription?.subscriptionUnit === 'DAY') {
      endsAt = startingTime.plus({ days: subscription?.subscriptionDuration });
    } else if (subscription?.subscriptionUnit === 'MONTH') {
      endsAt = startingTime.plus({
        months: subscription?.subscriptionDuration,
      });
    } else if (subscription?.subscriptionUnit === 'YEAR') {
      endsAt = startingTime.plus({ years: subscription?.subscriptionDuration });
    }
    return endsAt.toFormat('yyyy-LL-dd');
  };

  const getSuccessfulPurchases = async (fishId, status = null, purchaseArr = []) => {
    const successfulPurchases = [];
    if (fishId && status && Array.isArray(purchaseArr)) {
      for (const purchase of purchaseArr) {
        if (purchase?.updatedAt) {
          successfulPurchases.push({
            purchase: parseFloat(purchase.price),
            successDate: purchase.updatedAt,
          });
        }
      }
      return successfulPurchases;
    }
  };

  const getMemberSpendAmounts = async (members) => {
    if (members) {
      const membersArray = Array.isArray(members) ? members : [members];

      const now = DateTime.now();
      const oneYearAgo = now.minus({ years: 1 });

      const membersWithSpendAmounts = membersArray.map((member) => {
        const purchases = member?.purchasesArray ?? [];

        const allPurchasesTotal = purchases.reduce((sum, purchase) => {
          return sum + parseFloat(purchase.purchase ?? 0);
        }, 0);

        const yearToDatePurchases = purchases.filter((purchase) => {
          const successDate = DateTime.fromISO(purchase.successDate);
          return successDate >= oneYearAgo && successDate <= now;
        });

        const yearToDateSpend = yearToDatePurchases.reduce((sum, purchase) => {
          return sum + parseFloat(purchase.purchase ?? 0);
        }, 0);

        const startOfMonth = now.startOf('month');
        const monthToDatePurchases = purchases.filter((purchase) => {
          const successDate = DateTime.fromISO(purchase.successDate);
          return successDate >= startOfMonth && successDate <= now;
        });

        const monthToDateSpend = monthToDatePurchases.reduce(
          (sum, purchase) => {
            return sum + parseFloat(purchase.purchase ?? 0);
          },
          0,
        );

        return {
          ...member,
          allPurchasesTotal,
          yearToDateSpend,
          monthToDateSpend,
        };
      });

      return membersWithSpendAmounts;
    }
  };

  return (
    <FishContext.Provider
      value={{
        readAllFish,
        readFishById,
        readSegmentsForFish,
        readFishTags,
        fishArray,
        setFishArray,
        segmentsForFish,
        fishTags,
        getMemberById,
        formatNextWalletRenewal,
        nextReloadDate,
        setNextReloadDate,
        getNextSubscriptionRenewalDate,
        getSuccessfulPurchases,
        getMemberSpendAmounts,
      }}
    >
      {children}
    </FishContext.Provider>
  );
};

const useFish = () => useContext(FishContext);

export { useFish };
export default FishContextProvider;
